// @flow
import { all, call, put, takeEvery, takeLatest } from "redux-saga/effects";
import http from "@http";
import config from "@config";
import actionCreators from "@modules/integrations/actionCreator";
import * as actions from "@modules/integrations/constants/actions";
import { sendSuccessMessage } from "@modules/app/actionCreator";
import type { Action } from "@actionCreator";
import type {
  ApplicationRequest,
  ClientRequest,
  SaveApplicationRequest,
} from "@modules/integrations/types";

const applicationsUrl = (clientId: number) =>
  `${config.integrationBaseUrl}client/${clientId}/application`;
const applicationUrl = (clientId: number, id: number) => `${applicationsUrl(clientId)}/${id}`;
const secretUrl = (clientId: number, id: number) => `${applicationUrl(clientId, id)}/secret`;
const offlineTokenUrl = (clientId: number, id: number) =>
  `${applicationUrl(clientId, id)}/3rd-party-app-token`;

export function* onGetApplications({ payload }: Action<*, ClientRequest>): Generator<*, void, *> {
  try {
    const response = yield call(http.get, applicationsUrl(payload.clientId));
    yield put(actionCreators.GetIntegrationApplicationsSuccess(response.data));
  } catch (error) {
    yield put(actionCreators.GetIntegrationApplicationsError(error));
  }
}

export function* onCreateApplication({
  payload,
}: Action<*, SaveApplicationRequest>): Generator<*, void, *> {
  try {
    const response = yield call(http.post, applicationsUrl(payload.clientId), {
      ...payload.data,
      client: { id: payload.clientId },
    });
    yield all([
      put(actionCreators.CreateIntegrationApplicationSuccess(response.data)),
      put(sendSuccessMessage("INTEGRATION_APPLICATION_CREATED")),
      put(actionCreators.GetIntegrationApplications({ clientId: payload.clientId })),
    ]);
  } catch (error) {
    yield put(actionCreators.CreateIntegrationApplicationError(error));
  }
}

export function* onUpdateApplication({
  payload,
}: Action<*, SaveApplicationRequest>): Generator<*, void, *> {
  try {
    const response = yield call(
      http.put,
      applicationUrl(payload.clientId, payload.data.id),
      payload.data,
    );
    yield all([
      put(actionCreators.UpdateIntegrationApplicationSuccess(response.data)),
      put(sendSuccessMessage("INTEGRATION_APPLICATION_UPDATED")),
      put(actionCreators.GetIntegrationApplications({ clientId: payload.clientId })),
    ]);
  } catch (error) {
    yield put(actionCreators.UpdateIntegrationApplicationError(error));
  }
}

export function* onDeleteApplication({
  payload,
}: Action<*, ApplicationRequest>): Generator<*, void, *> {
  try {
    yield call(http.delete, applicationUrl(payload.clientId, payload.id));
    yield all([
      put(actionCreators.DeleteIntegrationApplicationSuccess()),
      put(sendSuccessMessage("INTEGRATION_APPLICATION_DELETED")),
      put(actionCreators.GetIntegrationApplications({ clientId: payload.clientId })),
    ]);
  } catch (error) {
    yield put(actionCreators.DeleteIntegrationApplicationError(error));
  }
}

export function* onGetApplicationDetails({
  payload,
}: Action<*, ApplicationRequest>): Generator<*, void, *> {
  try {
    const response = yield call(http.get, applicationUrl(payload.clientId, payload.id));
    yield put(actionCreators.GetIntegrationApplicationDetailsSuccess(response.data));
  } catch (error) {
    yield put(actionCreators.GetIntegrationApplicationDetailsError(error));
  }
}

export function* onGetSecretKey({ payload }: Action<*, ApplicationRequest>): Generator<*, void, *> {
  try {
    const response = yield call(http.get, secretUrl(payload.clientId, payload.id));
    yield put(actionCreators.GetIntegrationSecretKeySuccess(response.data));
  } catch (error) {
    yield put(actionCreators.GetIntegrationSecretKeyError(error));
  }
}

export function* onGenerateSecretKey({
  payload,
}: Action<*, ApplicationRequest>): Generator<*, void, *> {
  try {
    const response = yield call(http.post, secretUrl(payload.clientId, payload.id));
    yield put(actionCreators.GenerateIntegrationSecretKeySuccess(response.data));
  } catch (error) {
    yield put(actionCreators.GenerateIntegrationSecretKeyError(error));
  }
}

export function* onGenerateOfflineToken({
  payload,
}: Action<*, ApplicationRequest>): Generator<*, void, *> {
  try {
    const response = yield call(http.post, offlineTokenUrl(payload.clientId, payload.id), null);
    yield put(actionCreators.GenerateIntegrationOfflineTokenSuccess(response.data));
  } catch (error) {
    yield put(actionCreators.GenerateIntegrationOfflineTokenError(error));
  }
}

export function* onGetOfflineToken({
  payload,
}: Action<*, ApplicationRequest>): Generator<*, void, *> {
  try {
    const response = yield call(http.get, offlineTokenUrl(payload.clientId, payload.id));
    yield put(actionCreators.GetIntegrationOfflineTokenSuccess(response.data));
  } catch (error) {
    yield put(actionCreators.GetIntegrationOfflineTokenFailed(error));
    yield call(onGenerateOfflineToken, { type: actions.GenerateIntegrationOfflineToken, payload });
  }
}

export default function* integrationsSaga(): Generator<*, void, *> {
  yield all([
    takeLatest(actions.GetIntegrationApplications, onGetApplications),
    takeEvery(actions.CreateIntegrationApplication, onCreateApplication),
    takeEvery(actions.UpdateIntegrationApplication, onUpdateApplication),
    takeEvery(actions.DeleteIntegrationApplication, onDeleteApplication),
    takeLatest(actions.GetIntegrationApplicationDetails, onGetApplicationDetails),
    takeLatest(actions.GetIntegrationSecretKey, onGetSecretKey),
    takeLatest(actions.GenerateIntegrationSecretKey, onGenerateSecretKey),
    takeLatest(actions.GetIntegrationOfflineToken, onGetOfflineToken),
    takeLatest(actions.GenerateIntegrationOfflineToken, onGenerateOfflineToken),
  ]);
}
